import React, { useEffect } from 'react';
import 'react-quill/dist/quill.snow.css';
import { Controller, useFormContext } from 'react-hook-form';
import type { ControllerRenderProps, FieldError, FieldValues, RegisterOptions } from 'react-hook-form';
import { PendingUpload } from 'alurkerja-ui';
import { toast } from 'react-toastify';
import SwitchButton from './SwitchButton';
import { MarkdownInput } from '@/components/ui/MarkdownInput';
import RichTextEditor from '@/components/ui/RichTextEditor';
import { SubmitButton, SecondaryButton } from '@/components/ui/Button';

export interface FormField {
  name: string;
  label: string;
  type?:
    | 'text'
    | 'number'
    | 'date'
    | 'password'
    | 'select'
    | 'textarea'
    | 'switch'
    | 'upload'
    | 'richtext'
    | 'markdown'
    | string;
  uploadType?: 'image' | 'file' | 'both';
  allowedExtension?: string[];
  maxSize?: number;
  options?: { label: string; value: string }[];
  placeholder?: string;
  required?: boolean;
  onFileChange?: (file: File | null) => void;
  colSpan?: number;
  disabled?: boolean;
  labelRight?: React.ReactNode;
  hidden?: boolean;
  conditionalField?: string;
  conditionalValue?: string;
  hint?: string;
  render?: (props: FieldRenderProps) => React.ReactNode;
  rules?: RegisterOptions;
}

/** What `Controller` hands to a custom `FormField.render`. Only `error` is surfaced from fieldState. */
type RenderedField = ControllerRenderProps<FieldValues, string>;

type FieldRenderProps = {
  field: RenderedField;
  fieldState: { error?: FieldError };
};

type ControllerField = {
  value: unknown;
  onChange: (value: unknown) => void;
};

type UploadItem = {
  name?: string;
  original_name?: string;
  filename?: string;
  path?: string;
  size?: number | string;
  url?: string;
  file?: File;
};

function toUploadPreview(item: UploadItem) {
  return {
    name: item.name || item.original_name || item.filename || item.path,
    size: Number(item.size) || 1,
    url: item.url,
  };
}

function resolveUploadValue(value: unknown) {
  if (!value) return [];
  if (Array.isArray(value)) return (value as UploadItem[]).map(toUploadPreview);
  return [toUploadPreview(value as UploadItem)];
}

function extractFile(fileObj: unknown): File | null {
  if (fileObj instanceof File) return fileObj;
  if (fileObj && (fileObj as UploadItem).file instanceof File) return (fileObj as UploadItem).file as File;
  return null;
}

/** Shows a toast and returns false when the file violates the field's extension or size limit. */
function isFileAccepted(file: File, field: FormField): boolean {
  if (field.allowedExtension && field.allowedExtension.length > 0) {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!field.allowedExtension.includes(ext)) {
      toast.error(
        `Format file tidak didukung. Hanya file ${field.allowedExtension
          .map(e => e.toUpperCase())
          .join(', ')} yang diperbolehkan.`,
      );
      return false;
    }
  }
  if (field.maxSize !== undefined && file.size > field.maxSize) {
    toast.error(
      `Ukuran file terlalu besar. Maksimal ukuran file adalah ${Math.round(field.maxSize / (1024 * 1024))} MB.`,
    );
    return false;
  }
  return true;
}

function defaultExtensionFor(uploadType?: FormField['uploadType']): string[] {
  if (uploadType === 'image') return ['jpg', 'jpeg', 'png', 'webp'];
  if (uploadType === 'file') return ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt'];
  return [];
}

function UploadControl({ field, f, disabled }: Readonly<{ field: FormField; f: ControllerField; disabled: boolean }>) {
  const isBothUpload = field.uploadType === 'both';
  const allowedExtension = field.allowedExtension ?? defaultExtensionFor(field.uploadType);

  const handleChange = (files: unknown[]) => {
    const file = extractFile(files[0]);
    if (file && !isFileAccepted(file, field)) return;

    f.onChange(file);
    field.onFileChange?.(file);
  };

  return (
    <div>
      <PendingUpload
        disabled={disabled || field.disabled}
        asFile
        hideDownloadPreviewButton
        multiple={false}
        value={resolveUploadValue(f.value)}
        onChange={handleChange}
        allowedExtension={isBothUpload ? [] : allowedExtension}
        maxSize={field.maxSize}
      />

      <small className="text-gray-500 mt-1 block">
        Mendukung:{' '}
        {isBothUpload ? (
          <strong>semua jenis file (gambar, dokumen, BPMN, dll.)</strong>
        ) : (
          <strong>{allowedExtension.join(', ').toUpperCase()}</strong>
        )}{' '}
        {field.maxSize !== undefined && (
          <>
            {' '}
            · Maks. <strong>{Math.round(field.maxSize / (1024 * 1024))} MB</strong>
          </>
        )}
      </small>
    </div>
  );
}

/**
 * Native <select> bound to react-hook-form. When a field has no placeholder
 * option, the browser displays the first <option> as selected even though the
 * form value is still empty — so a submit without interaction loses the value.
 * Commit the first option to form state on mount to keep state and display in sync.
 */
function SelectField({
  field,
  f,
  disabled,
  dark,
}: {
  field: FormField;
  f: ControllerField;
  disabled: boolean;
  dark?: boolean;
}) {
  const options = field.options || [];
  const hasPlaceholder = !!field.placeholder;
  const valueMatchesOption = options.some(opt => opt.value === f.value);

  useEffect(() => {
    if (!hasPlaceholder && !valueMatchesOption && options.length > 0) {
      f.onChange(options[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPlaceholder, valueMatchesOption, options.length]);

  return (
    <select
      disabled={disabled || field.disabled}
      className={`text-sm w-full h-[38px] px-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none m-0 leading-tight ${
        dark
          ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-[#0D0D0D] text-gray-900 dark:text-white'
          : 'border-gray-300'
      }`}
      value={(f.value as string) ?? ''}
      onChange={e => f.onChange(e.target.value)}
      aria-label={field.label}
    >
      {hasPlaceholder && (
        <option value="" disabled hidden>
          {field.placeholder}
        </option>
      )}
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

interface FieldControlProps {
  field: FormField;
  f: RenderedField;
  error?: FieldError;
  disabled: boolean;
  dark: boolean;
}

function FieldControl({ field, f, error, disabled, dark }: Readonly<FieldControlProps>) {
  if (field.render) {
    return <>{field.render({ field: f, fieldState: { error } })}</>;
  }

  switch (field.type) {
    case 'date':
      return (
        <input
          {...f}
          disabled={disabled || field.disabled}
          type="date"
          className={`text-sm w-full px-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            dark
              ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-[#0D0D0D] text-gray-900 dark:text-white'
              : 'border-gray-300'
          }`}
          placeholder={field.placeholder || 'Pilih tanggal'}
        />
      );
    case 'number':
      return (
        <input
          {...f}
          disabled={disabled || field.disabled}
          type="number"
          className={`text-sm w-full h-[38px] px-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            dark
              ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-[#0D0D0D] text-gray-900 dark:text-white'
              : 'border-gray-300'
          }`}
          placeholder={field.placeholder || 'Masukkan angka'}
        />
      );
    case 'select':
      return <SelectField field={field} f={f} disabled={disabled} dark={dark} />;
    case 'textarea':
      return (
        <textarea
          {...f}
          rows={4}
          className={`text-sm w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            dark
              ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-[#0D0D0D] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500'
              : 'border-gray-300'
          }`}
          placeholder={field.placeholder}
        />
      );
    case 'switch':
      return (
        <div className="flex items-center justify-between border border-gray-200 rounded-xl p-2">
          <span className="text-sm">{field.label}</span>
          <SwitchButton checked={f.value} onChange={f.onChange} />
        </div>
      );
    case 'upload':
      return <UploadControl field={field} f={f} disabled={disabled} />;
    case 'richtext':
      return (
        <RichTextEditor
          value={f.value || ''}
          onChange={f.onChange}
          placeholder={field.placeholder || 'Tulis sesuatu...'}
        />
      );
    case 'markdown':
      return (
        <MarkdownInput
          value={f.value || ''}
          onChange={f.onChange}
          disabled={disabled || field.disabled}
          className="w-full mt-1 min-w-[180px]"
          placeholder={field.placeholder || 'Mendukung *tebal*, _miring_, ~~coret~~, `kode`'}
          dark={dark}
        />
      );
    default:
      return (
        <input
          disabled={disabled || field.disabled}
          {...f}
          type={typeof field.type === 'string' ? field.type : 'text'}
          className={`text-sm w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            dark
              ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-[#0D0D0D] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500'
              : 'border-gray-300'
          }`}
          placeholder={field.placeholder || field.label}
        />
      );
  }
}

const BUTTON_ALIGN_CLASS = {
  left: 'justify-start',
  right: 'justify-end',
  between: 'justify-between',
} as const;

interface FormBuilderWithLayoutProps {
  title?: string;
  description?: string;
  fields: FormField[];
  actions?: { label: string; variant?: 'primary' | 'outline'; onClick?: () => void }[];
  onSubmit?: () => void;
  onCancel?: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  initialPublished?: boolean;
  onPublishChange?: (value: boolean) => void;
  showPublishButton?: boolean;
  buttonAlign?: 'left' | 'right' | 'between';
  disabled?: boolean;
  columns?: 1 | 2;
  dark?: boolean;
}

export default function FormBuilderWithLayout({
  description,
  fields,
  onSubmit,
  onCancel,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  initialPublished = false,
  onPublishChange,
  showPublishButton = false,
  buttonAlign = 'left',
  disabled = false,
  columns = 2,
  dark = false,
}: Readonly<FormBuilderWithLayoutProps>) {
  const { control } = useFormContext();

  useEffect(() => {
    // Keep effect for potential future side-effects tied to initialPublished
  }, [initialPublished]);

  return (
    <div className="space-y-4 mx-5 md:mx-1">
      <div className="flex justify-between items-start">
        <div>{description && <h1 className="text-lg font-semibold">{description}</h1>}</div>
      </div>

      <div className={`grid ${columns === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'} gap-6`}>
        {fields
          .filter(field => !field.hidden)
          .map(field => (
            <div key={field.name} className={`col-span-${field.colSpan || 1} min-w-0`}>
              <div className="flex justify-between items-center mb-1">
                <label className={`block text-sm font-medium ${dark ? 'dark:text-gray-300' : ''}`}>
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </label>
                {field.labelRight && <div>{field.labelRight}</div>}
              </div>

              <Controller
                name={field.name}
                control={control}
                rules={{
                  ...(field.required ? { required: `${field.label} wajib diisi` } : {}),
                  ...(field.rules ?? {}),
                }}
                render={({ field: f, fieldState: { error } }) => (
                  <div>
                    <FieldControl field={field} f={f} error={error} disabled={disabled} dark={dark} />
                    {field.hint && <p className="text-xs text-gray-400 mt-1">{field.hint}</p>}
                    {error && <p className="text-xs text-red-500 mt-1">{error.message}</p>}
                  </div>
                )}
              />
            </div>
          ))}
      </div>

      {showPublishButton && (
        <Controller
          name="is_published"
          control={control}
          defaultValue={initialPublished}
          render={({ field }) => (
            <div className="flex gap-1 items-center">
              <SwitchButton
                checked={field.value}
                onChange={value => {
                  field.onChange(value);
                  onPublishChange?.(value);
                }}
                disabled={disabled}
              />
              <span className="text-sm font-semibold">Publikasikan</span>
            </div>
          )}
        />
      )}

      {(onSubmit || onCancel) && (
        <div className={`flex flex-col sm:flex-row gap-2 mt-6 ${BUTTON_ALIGN_CLASS[buttonAlign]}`}>
          {onCancel && (
            <div className="w-full sm:w-auto [&_button]:!w-full sm:[&_button]:!w-fit">
              <SecondaryButton onClick={onCancel}>{cancelLabel}</SecondaryButton>
            </div>
          )}
          {onSubmit && (
            <div className="w-full sm:w-auto [&_button]:!w-full sm:[&_button]:!w-fit">
              <SubmitButton onClick={onSubmit} className="bg-blue-600 hover:bg-blue-700 text-white">
                {submitLabel}
              </SubmitButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
